
import { useState } from "react";
import { CheckCircle, BarChart2, Activity, RefreshCw, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const features = [
  {
    id: "product-analysis",
    icon: <BarChart2 className="h-6 w-6" />,
    name: "E-commerce Product Analysis",
    description: "Access comprehensive product data from any e-commerce site worldwide. Monitor competitors' catalogs, detect new listings, and track inventory changes in real-time.",
    color: "from-benchr-700/20 to-benchr-500/5",
    benefits: [
      "Global product data coverage",
      "Catalog comparison tools",
      "New product alerts",
      "Product performance tracking"
    ]
  },
  {
    id: "pricing-intel",
    icon: <Activity className="h-6 w-6" />,
    name: "Competitive Pricing Intelligence",
    description: "Access pricing data from any e-commerce site globally. Track pricing strategies across competitors, detect promotional patterns, and optimize your pricing for maximum market impact.",
    color: "from-benchr-600/20 to-benchr-400/5",
    benefits: [
      "Global pricing data coverage",
      "Historical pricing data",
      "Discount & promotion detection",
      "Price elasticity analysis"
    ]
  },
  {
    id: "market-trends",
    icon: <RefreshCw className="h-6 w-6" />,
    name: "Market Trend Analysis",
    description: "Access traffic data and consumer behavior insights from any e-commerce platform worldwide. Analyze patterns and emerging market trends to stay ahead of the competition.",
    color: "from-benchr-500/20 to-benchr-300/5",
    benefits: [
      "Global market data coverage",
      "Consumer behavior insights",
      "Category growth tracking",
      "Seasonal trend prediction"
    ]
  },
  {
    id: "team-collaboration",
    icon: <Users className="h-6 w-6" />,
    name: "Team Collaboration Tools",
    description: "Share our global e-commerce data insights across your organization with customizable dashboards and reports, leveraging the most comprehensive retail dataset available.",
    color: "from-benchr-700/20 to-benchr-500/5",
    benefits: [
      "Customizable dashboards",
      "Scheduled reports",
      "Insight sharing tools",
      "Cross-department access controls"
    ]
  }
];

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(features[0]);

  return (
    <section 
      id="features" 
      className="py-12 relative overflow-hidden bg-gray-50 dark:bg-gray-900"
    >
      {/* Background patterns */}
      <div className="absolute inset-0 dark:bg-grid-dark bg-grid opacity-30 -z-10" />
      
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-4 tracking-tight animate-slide-up">
            Comprehensive E-commerce Competitive Intelligence Platform
          </h2>
          <p className="text-lg text-benchr-600 dark:text-benchr-300 animate-slide-up" style={{ animationDelay: "100ms" }}>
            Access detailed market data from any e-commerce site worldwide without building or maintaining any scraping infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Feature Cards - Column 1 and 2 */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <button
                key={feature.id}
                onClick={() => setActiveFeature(feature)}
                className={cn(
                  "text-left p-5 rounded-xl transition-all duration-300 ease-in-out flex items-start gap-4 group relative overflow-hidden h-full",
                  activeFeature.id === feature.id 
                    ? "bg-white dark:bg-benchr-800 shadow-md border border-benchr-100 dark:border-benchr-700" 
                    : "bg-white/80 dark:bg-benchr-900/80 hover:bg-white hover:shadow-sm dark:hover:bg-benchr-800/80 border border-transparent hover:border-benchr-100 dark:hover:border-benchr-700/50"
                )}
              >
                <div className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                  activeFeature.id === feature.id 
                    ? "bg-benchr-700 text-white" 
                    : "bg-benchr-100 dark:bg-benchr-800 text-benchr-700 dark:text-benchr-200 group-hover:bg-benchr-200 dark:group-hover:bg-benchr-700"
                )}>
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-benchr-900 dark:text-white text-lg mb-2">
                    {feature.name}
                  </h3>
                  <p className={cn(
                    "text-sm leading-relaxed",
                    activeFeature.id === feature.id 
                      ? "text-benchr-700 dark:text-benchr-200"
                      : "text-benchr-600 dark:text-benchr-400"
                  )}>
                    {feature.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Feature Details - Column 3 */}
          <div className="bg-white dark:bg-benchr-800 border border-benchr-100 dark:border-benchr-700 rounded-xl p-6 shadow-lg">
            <div className="flex flex-col h-full">
              <h3 className="text-xl font-bold text-benchr-900 dark:text-white mb-4">
                {activeFeature.name}
              </h3>
              <p className="text-benchr-700 dark:text-benchr-300 mb-6 text-md">
                {activeFeature.description}
              </p>
              
              <div className="mt-auto">
                <h4 className="text-md font-semibold text-benchr-900 dark:text-white mb-3">
                  Key Benefits
                </h4>
                <ul className="grid grid-cols-2 gap-y-2 gap-x-3">
                  {activeFeature.benefits.map((benefit, index) => (
                    <li 
                      key={index} 
                      className="flex items-start"
                    >
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5 mr-1.5" />
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="mt-6 pt-4 border-t border-benchr-100 dark:border-benchr-700/40">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-benchr-500 dark:text-benchr-400">
                    Data updated in real-time
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
